/** @license DAFT Core v1.2.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/
import { of, combineLatest, BehaviorSubject, Observable, Subscription } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import hash_it from 'hash-it';

import { Subject } from 'rxjs';
import { AnonymousSubject } from 'rxjs/internal/Subject';
import { DFSExpression, NIFormData } from './expressions';
import { DFSValueType } from './form';
import { isExpressionArray, isFieldName } from './processor'
import { spyFormValueChanges, isNullOrUndefined } from '..';
import { functions } from '.';

// this is not based off of their base, and is quite broken
export class ExpressionManager {

  // completed;
  private data$: BehaviorSubject<NIFormData>;
  private registry: { [key: string]: Observable<DFSValueType> | AnonymousSubject<DFSValueType> };
  private subscription: Subscription;
  private destroyed = new Subject();

  constructor(data$: Observable<NIFormData>) {
    this.data$ = new BehaviorSubject({});
    this.subscription = data$.subscribe(d => {
      return this.data$.next(d);
    });

    this.registry = {};
  }

  /**
   * Watch the parts of a given DFSExpression and stream the result.
   */
  public watch = (expression: DFSExpression | undefined, scope: string = ''): Observable<DFSValueType> | AnonymousSubject<DFSValueType> => {
    if (isNullOrUndefined(expression)) {
      return of(null);
    }

    var s = hashExpression(expression!, scope);
    var i = this.registry[s];

    if (!i) {

        if (isFieldName(expression!)) {
          // if the target is a field in the data schema, watch changes to that field
          i = this.data$.pipe(
            spyFormValueChanges(expression as string, scope),
            takeUntil(this.destroyed),
          )

        } else if (typeof expression === 'number' || typeof expression === 'boolean' || typeof expression === 'string') {
          // if the value is a primitive, just return it
          i = of(expression);
        } else if (Array.isArray(expression) && expression.length === 0) {
          // allow values to be set to an empty array
          i = of(expression!);
        } else if (isExpressionArray(expression!)) {
          i = combineLatest(expression.map(r => this.watch(r, scope)));
        } else if ((expression as any).op) {
          // if the target is a daft expression, pipe it through the appropriate handler. only do this if the object has an 'op' property,
          // otherwise vlaues can't be set to objects. 'op' must remain a reserved property on a DFS expression.
          var o = expression!.op;
          // typings messed up
          i = functions[o](expression, scope, this as any)
            .pipe(
              catchError(_ => {
                return of(null);
              })
            )
        } else {
          i = of(expression!);
        }

        this.registry[s] = i;
    }

    return i;
  };

  complete() {
    this.destroyed.next();
    this.destroyed.complete();
    this.subscription.unsubscribe();
    this.data$.complete();
    this.registry = {};
  };
}

function hashExpression(r: DFSExpression, e: string) {
  return hash_it({
    expression: r,
    scope: e
  })
}
